import React from 'react';
import { Search, Globe, Cog, TrendingUp, ArrowRight } from 'lucide-react';

const HowItWorksSection = () => {
  const steps = [
    {
      number: '01',
      icon: Search,
      title: 'Business Discovery',
      description: 'We understand your business, customers, target audience, and growth goals to create a tailored strategy.',
    },
    {
      number: '02',
      icon: Globe,
      title: 'AI Website Creation',
      description: 'Build a modern, AI-powered website optimized for your industry with fast loading times and mobile-first design.',
    },
    {
      number: '03',
      icon: Cog,
      title: 'Automation Setup',
      description: 'Add AI chatbots, WhatsApp automation, review requests, and lead capture tools to work 24/7.',
    },
    {
      number: '04',
      icon: TrendingUp,
      title: 'Growth Optimization',
      description: 'Continuously improve SEO, content, reviews, and conversion performance to maximize your ROI.',
    },
  ];

  return (
    <section 
      id="how-it-works"
      className="py-24 lg:py-32 bg-white"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100 mb-6">
            <Cog className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Simple Process</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            How It{' '}
            <span className="gradient-text">Works</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            We've made the process simple so you can focus on running your business while we handle the technical stuff.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connector Line - Hidden on mobile */}
          <div className="hidden lg:block absolute top-16 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-blue-200 via-violet-200 to-emerald-200" />
          
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative group"
              data-testid={`step-card-${index}`}
            >
              {/* Step Number Circle */}
              <div className="relative z-10 w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center mb-6 mx-auto lg:mx-0 shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-shadow">
                <span className="text-white font-bold text-lg">{step.number}</span>
              </div>
              
              {/* Arrow - Hidden on last item and mobile */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[calc(100%-8px)] z-20">
                  <ArrowRight className="w-5 h-5 text-slate-300" />
                </div>
              )}
              
              {/* Content */}
              <div className="text-center lg:text-left">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-4 mx-auto lg:mx-0 group-hover:bg-blue-50 transition-colors">
                  <step.icon className="w-6 h-6 text-slate-600 group-hover:text-blue-600 transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
